'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth, UserRole, ALL_ACCESS_ROLES } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';

// Shared role set for the unified Boards area (Sales / Production / Billing).
// Sales is the only restricted role; Admin/Manager are in ALL_ACCESS.
const BOARD_ROLES: UserRole[] = [...ALL_ACCESS_ROLES, 'Sales'];

// Route → required roles mapping.
// ALL_ACCESS_ROLES (Admin/Manager) are baked into every route's role list.
// Only routes that add 'Sales' are reachable by the Sales role; '/...': ['Admin']
// entries are system-administration pages restricted to Admin.
export const ROUTE_ROLE_MAP: Record<string, UserRole[]> = {
  // Operational dashboards (Admin + Manager)
  '/project-manager-dashboard':              [...ALL_ACCESS_ROLES],
  '/quality-control-dashboard':              [...ALL_ACCESS_ROLES],
  // Personal sales dashboard — Sales reps + full-access roles (filters to own book)
  '/sales-dashboard':                        [...ALL_ACCESS_ROLES, 'Sales'],
  '/logistics-supply-chain-dashboard':       [...ALL_ACCESS_ROLES],
  '/operations-command-center-dashboard':    [...ALL_ACCESS_ROLES],
  '/financial-accounting-dashboard':         [...ALL_ACCESS_ROLES],
  '/executive-command-center-dashboard':     [...ALL_ACCESS_ROLES],
  '/warranty-claims-dashboard':              [...ALL_ACCESS_ROLES],
  '/escalation-management-dashboard':        [...ALL_ACCESS_ROLES],
  '/reports-analytics-dashboard':            [...ALL_ACCESS_ROLES],

  // HR & employee
  '/hr':                                     [...ALL_ACCESS_ROLES],
  '/employee-directory':                     [...ALL_ACCESS_ROLES],
  '/permits':                                [...ALL_ACCESS_ROLES],

  // Production / Crew
  '/production-board':                        BOARD_ROLES,
  '/billing-board':                           BOARD_ROLES,
  '/installer-network-crew-management-dashboard': [...ALL_ACCESS_ROLES],
  '/state-specific-crew-management-view':    [...ALL_ACCESS_ROLES],
  '/job-detail-management-panel':            [...ALL_ACCESS_ROLES],

  // Customer-facing (Sales-accessible)
  '/customer-dashboard':                     BOARD_ROLES,
  '/estimates':                              BOARD_ROLES,
  '/customers':                              [...ALL_ACCESS_ROLES, 'Sales'],
  '/contacts':                               [...ALL_ACCESS_ROLES, 'Sales'],

  // Storm / hail intelligence (sales + lead-gen tool)
  '/storm-map':                              [...ALL_ACCESS_ROLES, 'Sales'],

  // Strategy / Expansion
  '/expansion-market-intelligence-dashboard': [...ALL_ACCESS_ROLES],
  '/admin/national-expansion-map':           [...ALL_ACCESS_ROLES],
  '/admin/city-analytics':                   [...ALL_ACCESS_ROLES],

  // Photos
  '/photo-gallery-approval-workflow-system': [...ALL_ACCESS_ROLES],

  // Admin-only (system administration)
  '/role-based-navigation-module-management': ['Admin'],
  '/add-user-management-page':               ['Admin'],
  '/hr-storage-setup':                       ['Admin'],
  '/hr-cleanup-storage':                     ['Admin'],
  '/hr-smoke-test':                          ['Admin'],
  '/pto-smoke-test':                         ['Admin'],
  '/permits-smoke-test':                     ['Admin'],
  '/purchase-orders-smoke-test':             ['Admin'],
  '/rbac-access-smoke-test':                 ['Admin'],
  '/rbac-dashboard-schema-discovery':        ['Admin'],
  '/rbac-dashboard-filter-smoke-test':       ['Admin'],

  // Universal pages intentionally NOT in this map:
  // /me, /interactive-calendar-schedule-management, /virtual-meetings-customer-communication-hub
};

// Role → home dashboard mapping
export const ROLE_HOME_MAP: Record<UserRole, string> = {
  Admin:                  '/executive-command-center-dashboard',
  Manager:                '/operations-command-center-dashboard',
  Sales:                  '/customer-dashboard',
};

interface RouteGuardProps {
  route: string;
  children: React.ReactNode;
}

export default function RouteGuard({ route, children }: RouteGuardProps) {
  const { currentUser, authLoading, isAuthenticated } = useAuth();
  const { getEffectiveRoles, getEffectiveIsSuperAdmin } = useImpersonation();
  const router = useRouter();
  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  // Ref tracks whether we're still in the checking state — safe to read inside setTimeout
  const checkingRef = useRef(true);

  // Keep ref in sync with state
  useEffect(() => {
    checkingRef.current = checking;
  }, [checking]);

  // Safety net: if auth never resolves within 8s, redirect to login
  useEffect(() => {
    const timer = setTimeout(() => {
      if (checkingRef.current) {
        setChecking(false);
        router.replace('/login-authentication-screen');
      }
    }, 8000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  useEffect(() => {
    if (authLoading) return;

    // Not authenticated — redirect to login
    if (!isAuthenticated) {
      setTimeout(() => router.replace('/login-authentication-screen'), 0);
      return;
    }

    const effectiveRoles = getEffectiveRoles(currentUser.roles);
    const effectiveIsSuperAdmin = getEffectiveIsSuperAdmin(effectiveRoles.includes('Admin'));

    // SuperAdmin bypasses all guards
    if (effectiveIsSuperAdmin) {
      setAuthorized(true);
      setChecking(false);
      return;
    }

    const requiredRoles = ROUTE_ROLE_MAP[route];
    if (!requiredRoles) {
      // No guard defined for this route — allow access (universal pages)
      setAuthorized(true);
      setChecking(false);
      return;
    }

    const hasAccess = effectiveRoles.some((r) => requiredRoles.includes(r));

    if (hasAccess) {
      setAuthorized(true);
      setChecking(false);
    } else {
      // Find the home dashboard for the user's primary effective role
      const primaryRole = effectiveRoles[0] as UserRole;
      router.replace(ROLE_HOME_MAP[primaryRole] ?? '/me');
      // Keep checking=true so we show the loader while redirecting
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, currentUser, route, router]);

  if (checking || !authorized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-[3px] border-[#E89A3C] border-t-transparent animate-spin" />
          <span className="text-sm text-gray-500 font-medium">Verifying access...</span>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
